import { useEffect, useMemo } from "react";
import {
  type ICommercialFeedDataGridRow,
  type ICommercialFeedFetchParams,
} from "../types";
import { useCommercialFeed } from "./useCommercialFeed";
import { toCommercialFeedGridRows } from "../utils/feedUtils";

// useCommercialGridFeedに渡すパラメータの型
type IUseCommercialGridFeedParams = Omit<
  ICommercialFeedFetchParams,
  "size" | "fromCount"
> & {
  page: number;
  pageSize: number;
};

interface IUseCommercialGridFeedReturnType {
  rows: ICommercialFeedDataGridRow[];
  allCount: number;
  loading: boolean;
  from: number;
  prev: () => void;
  next: () => void;
}

interface IUseCommercialGridFeedOptions {
  enabled?: boolean;
}

const FEED_FETCH_SIZE = 1000;

/**
 * useCommercialFeedの結果を、DataGridに表示するためのデータに変換して返すカスタムフック
 */
export const useCommercialGridFeed = (
  params: IUseCommercialGridFeedParams,
  options: IUseCommercialGridFeedOptions = { enabled: true }
): IUseCommercialGridFeedReturnType => {
  const { page, pageSize, ...feedParams } = params;
  const { feed, allCount, from, prev, next, isLoading } = useCommercialFeed(
    {
      ...feedParams,
      size: FEED_FETCH_SIZE,
    },
    options
  );

  // 現在取得済みの件数よりも、表示するページの件数が多い場合は、次のページを取得する
  useEffect(() => {
    const pageNumberForCalcDataset = page + 1;
    if (feed === null || feed === undefined) return;
    if (pageNumberForCalcDataset * pageSize <= feed.list.length) return;
    next();
  }, [page, pageSize, feed, next]);

  // feedが変化しない限りrowsの参照を維持する（呼び出し側のuseEffectの依存に使われるため）
  const rows = useMemo(() => toCommercialFeedGridRows(feed), [feed]);

  return {
    rows,
    allCount,
    loading: isLoading,
    from,
    prev,
    next,
  };
};
